/**
 * Loading Google's Identity Services script, and the sliver of it we call.
 *
 * The script is fetched from Google at sign-in time rather than bundled,
 * because Google's terms require the button be their code and their rendering.
 * It is the one third-party script this client loads, and it loads only on the
 * login screen: a studio with no `googleClientId` never asks for it.
 */

export type GoogleCredentialResponse = { credential?: string };

type IdentityServices = {
  accounts: {
    id: {
      initialize(config: {
        client_id: string;
        callback: (response: GoogleCredentialResponse) => void;
        auto_select?: boolean;
        cancel_on_tap_outside?: boolean;
      }): void;
      renderButton(
        parent: HTMLElement,
        options: {
          theme?: 'outline' | 'filled_blue' | 'filled_black';
          size?: 'small' | 'medium' | 'large';
          text?: 'signin_with' | 'signup_with' | 'continue_with';
          shape?: 'rectangular' | 'pill';
          logo_alignment?: 'left' | 'center';
          width?: number;
        },
      ): void;
    };
  };
};

declare global {
  interface Window {
    google?: IdentityServices;
  }
}

const SRC = 'https://accounts.google.com/gsi/client';

let loading: Promise<IdentityServices> | null = null;

/**
 * The Identity Services object, loading the script on first call
 *
 * Cached as the PROMISE rather than the result, so a second caller arriving
 * mid-load waits on the same script tag instead of appending another.
 *
 * @returns `Promise<IdentityServices>`: resolved once `window.google` exists
 * @throws `Error`: the script could not be fetched, which on a network this
 *   locked down is the likeliest failure and has to leave the password path
 *   reachable rather than blanking the page
 */
export function loadGoogleIdentity(): Promise<IdentityServices> {
  if (loading) return loading;
  loading = new Promise<IdentityServices>((resolve, reject) => {
    if (window.google) return resolve(window.google);
    const script = document.createElement('script');
    script.src = SRC;
    script.async = true;
    script.onload = () => {
      if (window.google) resolve(window.google);
      else reject(new Error('Google Identity Services loaded without defining itself'));
    };
    script.onerror = () => {
      // Cleared so a retry can try again; a rejected cached promise would make
      // the first network blip permanent for the life of the tab.
      loading = null;
      reject(new Error('Could not load Google Identity Services'));
    };
    document.head.appendChild(script);
  });
  return loading;
}
